import { z } from 'zod';

/* ------------------------------------------------------------------ */
/* Tag ids: Crockford base32, 10 characters (50 random bits).          */
/* No I, L, O or U, so ids survive being read aloud or typed by hand.  */
/* ------------------------------------------------------------------ */

export const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const TAG_ID_LENGTH = 10;
const TAG_ID_RE = new RegExp(`^[${CROCKFORD}]{${TAG_ID_LENGTH}}$`);

/** Cryptographically random tag id. Works in Node 22 and browsers. */
export function newTagId(): string {
  return randomCrockford(TAG_ID_LENGTH);
}

/** Random secret for an owner link (25 chars, 125 bits). */
export function newOwnerKey(): string {
  return randomCrockford(25);
}

function randomCrockford(length: number): string {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  let out = '';
  // 256 is a multiple of 32, so taking the low 5 bits has no modulo bias.
  for (const b of bytes) out += CROCKFORD[b & 31];
  return out;
}

/**
 * Normalize anything a human or a QR scanner might produce into a canonical id.
 * Case-insensitive, ignores spaces and dashes, maps the usual look-alikes.
 * Returns null when the result is not a valid id.
 */
export function normalizeTagId(input: string): string | null {
  const s = input
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0');
  return TAG_ID_RE.test(s) ? s : null;
}

/** Human-readable short code printed under the QR: ABCDE-FGHJK. */
export function formatShortCode(tagId: string): string {
  return `${tagId.slice(0, 5)}-${tagId.slice(5)}`;
}

/* ------------------------------------------------------------------ */
/* Found reports                                                       */
/* ------------------------------------------------------------------ */

export const REPORT_KINDS = ['location', 'lost_and_found', 'staff', 'unsure'] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

export const MESSAGE_MAX = 280;

export const reportInput = z
  .object({
    kind: z.enum(REPORT_KINDS),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    accuracy: z.number().min(0).max(100_000).optional(),
    message: z.string().max(2000).optional(),
  })
  .refine((r) => r.kind !== 'location' || (r.lat !== undefined && r.lng !== undefined), {
    message: 'location reports need lat and lng',
  });
export type ReportInput = z.infer<typeof reportInput>;

/** What the owner sees. Never contains anything about the finder. */
export interface FoundReport {
  id: string;
  kind: ReportKind;
  createdAt: string;
  /** Set when the owner confirmed they got the item back. */
  returnedAt?: string;
  /** Level name of the helper who reported it, if they had a helper card. */
  helperLevel?: string;
  lat?: number;
  lng?: number;
  accuracy?: number;
  message?: string;
}

/** Links and control characters out, whitespace collapsed, length capped. */
export function sanitizeMessage(input: string | undefined): string | undefined {
  if (!input) return undefined;
  const cleaned = input
    .replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, ' ')
    .replace(/\b(?:https?:\/\/|www\.)\S+/gi, '')
    .replace(/\b[\w.-]+\.(?:com|se|app|net|org|io|nu|info|biz|xyz|ly|me|co)(?:\/\S*)?/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MESSAGE_MAX);
  return cleaned.length > 0 ? cleaned : undefined;
}

/** Rounds coordinates to about 1 m. More precision only adds noise. */
export function roundCoord(value: number): number {
  return Math.round(value * 1e5) / 1e5;
}

export function mapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

/* ------------------------------------------------------------------ */
/* API shapes                                                          */
/* ------------------------------------------------------------------ */

/** Public tag lookup. Unknown and deactivated ids look the same on purpose. */
export interface TagStatusResponse {
  status: 'active' | 'unavailable';
}

export const demoTagInput = z.object({
  label: z.string().max(200).optional(),
  notifyEmail: z.string().max(254).optional(),
});

export interface CreateDemoTagResponse {
  tagId: string;
  ownerKey: string;
  label: string;
  expiresAt: string;
}

/** Finder URL as encoded in QR codes: uppercase, so the encoder can use alphanumeric mode. */
export function qrFinderUrl(origin: string, tagId: string): string {
  return `${origin.replace(/\/+$/, '').toUpperCase()}/T/${tagId}`;
}

export interface OwnerReportsResponse {
  tagId: string;
  label: string;
  /** Masked, e.g. he***@rollwise.io. Present when email pings are on. */
  notifyEmail?: string;
  /** Set for self-serve demo tags. */
  demoExpiresAt?: string;
  reports: FoundReport[];
}

/* ------------------------------------------------------------------ */
/* Helpers (finders) and points                                        */
/* ------------------------------------------------------------------ */
/*
 * Finders stay anonymous. A helper card is a random secret kept in the
 * finder's browser. The server stores only its hash and a points tally.
 * Points for a find are small; the big reward comes when the owner confirms
 * the item is back, which makes farming by fake scans pointless.
 */

export const POINTS = { find: 10, returned: 50 } as const;

export const LEVELS = [
  { min: 0, name: 'Rookie' },
  { min: 30, name: 'Spotter' },
  { min: 100, name: 'Good Neighbour' },
  { min: 250, name: 'Lost & Found Hero' },
  { min: 600, name: 'Legend' },
] as const;

export interface LevelInfo {
  level: number;
  name: string;
  /** Points needed for the next level, or null at the top. */
  nextAt: number | null;
  nextName: string | null;
  /** 0 to 1 progress inside the current level. */
  progress: number;
}

export function levelFor(points: number): LevelInfo {
  let i = 0;
  for (let k = 0; k < LEVELS.length; k++) if (points >= LEVELS[k]!.min) i = k;
  const current = LEVELS[i]!;
  const next = LEVELS[i + 1];
  return {
    level: i + 1,
    name: current.name,
    nextAt: next ? next.min : null,
    nextName: next ? next.name : null,
    progress: next ? (points - current.min) / (next.min - current.min) : 1,
  };
}

export const BADGES = [
  { id: 'first_find', name: 'First find', test: (p: HelperStats) => p.finds >= 1 },
  { id: 'first_return', name: 'Back home', test: (p: HelperStats) => p.returns >= 1 },
  { id: 'reliable', name: 'Reliable', test: (p: HelperStats) => p.returns >= 3 },
  { id: 'sharp_eye', name: 'Sharp eye', test: (p: HelperStats) => p.finds >= 10 },
  { id: 'hero', name: 'Hero', test: (p: HelperStats) => p.points >= 250 },
] as const;
export type BadgeId = (typeof BADGES)[number]['id'];

export interface HelperStats {
  points: number;
  finds: number;
  returns: number;
}

export function badgesFor(stats: HelperStats): BadgeId[] {
  return BADGES.filter((b) => b.test(stats)).map((b) => b.id);
}

export const HELPER_ID_RE = /^[0-9A-HJKMNP-TV-Z]{25}$/;
export const NICKNAME_MAX = 20;

export interface HelperProfileResponse extends HelperStats {
  nickname?: string;
  badges: BadgeId[];
}

/** What the finder sees after a report. `awarded` is 0 when no points were due. */
export interface ReportResponse {
  ok: true;
  helper?: HelperProfileResponse & { awarded: number };
}

export interface LeaderboardResponse {
  entries: Array<{ nickname: string; points: number }>;
}

export const nicknameInput = z.object({ nickname: z.string().max(100) });

/** Nicknames: letters, digits, spaces, dot, dash, underscore. Never links. */
export function sanitizeNickname(input: string): string | undefined {
  const s = input
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N} ._-]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, NICKNAME_MAX);
  return s.length >= 2 ? s : undefined;
}

export function maskEmail(email: string): string {
  const [user = '', domain = ''] = email.split('@');
  return `${user.slice(0, 2)}***@${domain}`;
}
